import React, { useState, useEffect } from 'react';
import { Form, InputField, RegularTextAreaField } from 'components/Form';
import { Button, Dialog, DialogTitle, Spinner } from 'components/Elements';
import { useDepositToWallet } from '../api/depositToWallet';
import { searchCustomers } from 'features/users';
import { X } from 'react-feather';
import { getCustomerWallets } from '../api/getCustomerWallets';
import { WalletCard } from './WalletCard';
import Lottie from 'lottie-react';
import checkAnimation from 'assets/lottie/check.json';
import { ArrowUp } from 'lucide-react';
import DepositSvg from 'assets/deposit.svg';

export const DepositToCustomerWallet = ({
  depositPage,
  customerId: defaultId,
  customerName: defaultName,
}) => {
  const [show, setShow] = useState(false);
  const [customerId, setCustomerId] = useState(defaultId);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [walletResults, setWalletResults] = useState(null);
  const [walletError, setWalletError] = useState(null);
  const [isFetchingWallets, setIsFetchingWallets] = useState(false);
  const [customerName, setCustomerName] = useState(defaultName);
  const [clientWalletId, setClientWalletId] = useState(null);
  const [currencyId, setCurrencyId] = useState(null);
  const [currencyCode, setCurrencyCode] = useState(null);

  const depositToWalletMutation = useDepositToWallet();

  useEffect(() => {
    if (customerId) {
      setIsFetchingWallets(true);
      getCustomerWallets({ customerId }).then((result) => {
        setWalletResults(result?.walletDetails);
        setIsFetchingWallets(false);
      });
      setCustomerName(defaultName);
    }
  }, [customerId, defaultName]);

  useEffect(() => {
    if (show) {
      setCustomerId(defaultId);
      setCustomerName(defaultName);
    }
  }, [show, defaultId, defaultName]);

  const handleClose = () => {
    setShow(false);
    setSearchResults(null);
    setSearchText('');
    setWalletResults(null);
    setCustomerId(null);
    setCustomerName(null);
    setClientWalletId(null);
    setCurrencyCode(null);
    setCurrencyId(null);
    setShowSuccessMessage(false);
  };

  const handleOpen = () => setShow(true);

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    setWalletError(null);
    setIsLoading(true);
    setSearchResults(null);

    if (searchText.trim()) {
      try {
        const response = await searchCustomers({
          text: searchText,
          customerId: 0,
        });
        setIsLoading(false);
        if (response?.users?.length > 0) {
          setSearchResults(response);
        }
      } catch (error) {
        console.error('Error searching for admin:', error);
      }
    }
  };

  return (
    <div>
      <Button
        onClick={handleOpen}
        startIcon={
          <img src={DepositSvg} alt="deposit" className="w-5 h-5 mr-2" />
        }
        size="sm"
        variant="darkGlass"
      >
        Add to wallet
      </Button>

      <Dialog isOpen={show} onClose={handleClose} initialFocus={null}>
        <div className="bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] shadow-2xl rounded-2xl p-6  w-full text-white inline-block align-bottom px-4 pt-5 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div className="flex flex-row justify-between items-center mb-3">
            <DialogTitle className="text-xl text-start text-white font-bold">
              {showSuccessMessage === true ? '' : 'Add to user wallet'}
            </DialogTitle>
            <button onClick={handleClose}>
              <X />
            </button>
          </div>
          {isFetchingWallets ? (
            <div className="flex flex-row items-center space-x-2 justify-center">
              <Spinner size="md" />
            </div>
          ) : (
            <>
              {showSuccessMessage ? (
                <div className="flex flex-col items-center">
                  <Lottie
                    animationData={checkAnimation}
                    className="w-48"
                    loop
                  />
                  <div className="mt-4 text-center">
                    <h2 className="text-2xl font-bold text-green-600 dark:text-green-400">
                      Deposit Completed!
                    </h2>
                    <p className="mt-2 text-white">
                      You’ve just credited{' '}
                      <span className="capitalize font-bold text-green-600 dark:text-green-400">
                        {customerName}'s
                      </span>{' '}
                      wallet with the funds.
                    </p>
                    <p className="mt-1 text-white">
                      Their balance is now updated and ready to go. <br />
                      Thank you for your deposit.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {customerId ? (
                    <>
                      {walletResults ? (
                        <>
                          <div className="text-md font-semibold capitalize">
                            {customerName}'s wallets.
                          </div>
                          <div className="text-sm font-light mb-3 ">
                            Select a wallet to deposit
                          </div>
                          {walletResults.map((wallet) => (
                            <button
                              type="button"
                              className="w-full mb-2"
                              onClick={() => {
                                setClientWalletId(wallet.id);
                                setCurrencyId(wallet.currencyId);
                                setCurrencyCode(wallet.currencyCode);
                                setWalletResults(null);
                              }}
                            >
                              <WalletCard key={wallet.id} wallet={wallet} />
                            </button>
                          ))}
                        </>
                      ) : (
                        <Form
                          id="deposit-to-wallet"
                          onSubmit={async (values) => {
                            const data = {
                              clientWalletId,
                              clientId: customerId,
                              currencyId,
                              currencyCode,
                              ...values,
                            };
                            const result =
                              await depositToWalletMutation.mutateAsync({
                                data,
                              });
                            if (result) {
                              setShowSuccessMessage(true);
                            }
                          }}
                        >
                          {({ register, formState }) => (
                            <>
                              <div className="text-md font-bold capitalize">
                                Provide deposit amount.
                              </div>
                              <InputField
                                label="Amount"
                                error={formState.errors['amount']}
                                registration={register('amount')}
                              />

                              <RegularTextAreaField
                                label="Reason"
                                error={formState.errors['reason']}
                                registration={register('reason')}
                              />

                              <div className="mt-2 flex flex-row items-center space-x-2">
                                <Button
                                  className="w-full"
                                  isLoading={depositToWalletMutation.isLoading}
                                  type="submit"
                                  variant="glass"
                                >
                                  Continue
                                </Button>
                              </div>
                            </>
                          )}
                        </Form>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-lg font-semibold">
                        Search for user
                      </div>
                      <div className="text-md mb-3">
                        Search and select a user to preview the wallets
                      </div>
                      <form
                        onSubmit={handleSearchSubmit}
                        className="flex justify-center items-center"
                      >
                        <input
                          type="text"
                          placeholder="Search for user's name..."
                          className="text-sm mx-auto w-full px-4 shadow dark:shadow-none py-4 rounded-full focus:outline-none border border-gray-100 dark:border-gray-800 dark:bg-gray-700 text-gray-800 dark:text-white"
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                        />
                      </form>
                      <div className="py-4">
                        {searchResults ? (
                          <>
                            {isLoading ? (
                              <div className="flex flex-row items-center space-x-2 justify-center">
                                <Spinner size="sm" />{' '}
                                <div className="text-sm">Searching..</div>
                              </div>
                            ) : (
                              <>
                                {searchResults.users.map((user) => (
                                  <div className="mb-4">
                                    <button
                                      onClick={async () => {
                                        setIsLoading(true);
                                        const result = await getCustomerWallets(
                                          {
                                            customerId: user.id,
                                          }
                                        );
                                        if (
                                          result?.walletDetails?.length <= 0
                                        ) {
                                          setIsLoading(false);
                                          setWalletError(
                                            'This user has no wallets.'
                                          );
                                        } else {
                                          setCustomerId(user.id);
                                          setCustomerName(
                                            `${user.firstName} ${user.lastName}`
                                          );
                                          setIsLoading(false);
                                          setWalletResults(
                                            result?.walletDetails
                                          );
                                        }
                                      }}
                                      type="button"
                                      key={user.id}
                                      className={`p-1 flex items-center border ${
                                        walletError
                                          ? 'border-red-400'
                                          : 'border-gray-100 dark:border-gray-700'
                                      }   hover:bg-gray-100 dark:hover:bg-gray-700 rounded-3xl w-full`}
                                    >
                                      <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center overflow-hidden">
                                        {user.photo ? (
                                          <img
                                            src={user.photo}
                                            alt={`${user.firstName} ${user.lastName}`}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <span className="text-gray-500 ">
                                            No Photo
                                          </span>
                                        )}
                                      </div>
                                      <div className="ml-4">
                                        <p className="text-lg text-start font-medium">
                                          {user.firstName} {user.lastName}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-400">
                                          {user.email || 'No email provided'}
                                        </p>
                                        {user.phoneNumber && (
                                          <p className="text-gray-600 dark:text-gray-400">
                                            {user.phoneNumber}
                                          </p>
                                        )}
                                      </div>
                                    </button>
                                    <div className="text-red-500 mt-2 text-center">
                                      {walletError ? walletError : null}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </>
                        ) : (
                          <></>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
};
